/**
 * SlaCards — tres tarjetas de cumplimiento del día:
 *   · SLA mismo día        → recolectado y entregado el mismo día calendario.
 *   · SLA antes de 21:00    → entregado antes del cutoff en la TZ del operador.
 *   · Paquetes en riesgo    → no entregados y cerca del cutoff (endpoint nuevo).
 *
 * Los % de SLA salen de operational-summary (microservice-reports). El riesgo
 * sale de /reports/dashboard/at-risk; mientras el backend no responda, la
 * tarjeta muestra un aviso de pendiente en vez de un número inventado.
 */
import { useTranslation } from 'react-i18next';
import { CalendarCheck, Clock, AlertTriangle } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Skeleton } from '../../../components/ui/Skeleton';
import { PendingBackendNotice } from '../../../components/ui/PendingBackendNotice';
import { formatNumberCL } from '../../../utils/formatters';
import { cn } from '../../../utils/cn';

function pctColor(pct) {
  if (pct >= 90) return 'rgb(var(--status-success))';
  if (pct >= 75) return 'rgb(var(--status-warning))';
  return 'rgb(var(--status-error))';
}

function Ring({ pct, color }) {
  const clamped = Math.min(Math.max(pct, 0), 100);
  return (
    <span
      className="relative grid h-20 w-20 shrink-0 place-items-center rounded-full"
      style={{ background: `conic-gradient(${color} ${clamped}%, rgb(var(--bg-subtle)) 0)` }}
      aria-hidden
    >
      <span className="grid h-14 w-14 place-items-center rounded-full bg-bg">
        <span className="text-lg font-bold tabular-nums text-fg">{Math.round(clamped)}%</span>
      </span>
    </span>
  );
}

function PctCard({ icon: Icon, title, pct, numerator, denominator, subLabel, loading }) {
  const { t } = useTranslation();
  if (loading) return <Skeleton className="h-32 rounded-xl" />;
  const color = pctColor(pct);
  return (
    <Card padding="md">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-bg-muted text-fg-muted">
          <Icon className="h-4 w-4" strokeWidth={2.2} />
        </span>
        <h3 className="text-sm font-semibold text-fg">{t(title)}</h3>
      </div>
      <div className="flex items-center gap-4">
        <Ring pct={pct} color={color} />
        <div className="min-w-0">
          <div className="text-lg font-semibold text-fg">
            {formatNumberCL(numerator)} <span className="font-normal text-fg-muted">{t('de')} {formatNumberCL(denominator)}</span>
          </div>
          <div className="mt-1 text-xs text-fg-subtle">{t(subLabel)}</div>
        </div>
      </div>
    </Card>
  );
}

export default function SlaCards({ op, kpis, atRisk, loading }) {
  const { t } = useTranslation();

  // SLA antes de 21:00 — ya viene calculado (serviceLevel.deliveredBeforeCutoff).
  const slPct = kpis?.serviceLevelPct ?? 0;
  const slBefore = kpis?.serviceLevelBefore ?? 0;
  const slTotal = kpis?.serviceLevelTotal ?? 0;

  // SLA mismo día — el bloque sameDayAttention de operational-summary ahora trae
  // tanto el puro mismo-día (sameDayDelivered/sameDayPct, la definición acordada)
  // como el legacy mismo-día-antes-de-cutoff (sameDayBeforeCutoff/attentionPct).
  // Preferimos el puro; si el backend aún no lo expone, caemos al legacy.
  const sd = op?.sameDayAttention || null;
  const sdPct = sd ? (sd.sameDayPct ?? sd.attentionPct ?? 0) : 0;
  const sdNum = sd ? (sd.sameDayDelivered ?? sd.sameDayBeforeCutoff ?? 0) : 0;
  const sdTotal = sd ? (sd.totalCollected ?? 0) : 0;

  // Riesgo
  const riskColor =
    atRisk?.critical > 0
      ? 'text-status-error'
      : atRisk?.count > 0
        ? 'text-status-warning'
        : 'text-status-success';

  return (
    <>
      <PctCard
        icon={CalendarCheck}
        title="SLA mismo día"
        pct={sdPct}
        numerator={sdNum}
        denominator={sdTotal}
        subLabel="recolectados y entregados hoy"
        loading={loading}
      />
      <PctCard
        icon={Clock}
        title="SL antes de las 21:00"
        pct={slPct}
        numerator={slBefore}
        denominator={slTotal}
        subLabel="entregados antes del cutoff"
        loading={loading}
      />

      {/* Paquetes en riesgo */}
      {loading || atRisk?.loading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : (
        <Card padding="md">
          <div className="mb-3 flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-status-warning-soft text-status-warning">
              <AlertTriangle className="h-4 w-4" strokeWidth={2.2} />
            </span>
            <h3 className="text-sm font-semibold text-fg">{t('Paquetes en riesgo')}</h3>
          </div>
          {atRisk?.available ? (
            <div className="flex items-end gap-4">
              <span className={cn('text-5xl font-bold tabular-nums leading-none', riskColor)}>
                {formatNumberCL(atRisk.count)}
              </span>
              <div className="min-w-0 pb-1.5">
                {atRisk.critical > 0 && (
                  <div className="text-sm font-semibold text-status-error">
                    {formatNumberCL(atRisk.critical)} {t('críticos (≤1 h)')}
                  </div>
                )}
                <div className="text-xs text-fg-subtle">
                  {t('no entregados, cerca de las 21:00')}
                </div>
              </div>
            </div>
          ) : (
            <PendingBackendNotice>
              {t('El cálculo de riesgo por hora de corte está en construcción.')}
            </PendingBackendNotice>
          )}
        </Card>
      )}
    </>
  );
}
